import clsx from 'clsx'
import { computed, type DeepReadonly, defineComponent } from 'vue'

import { RouterLink } from 'vue-router'

import VButton from '@/components/common/button/VButton'
import PromptModal from '@/components/common/modal/PromptModal'
import VTableBodyCell from '@/components/common/table/VTableBodyCell'
import VChip from '@/components/common/VChip'
import VIcon from '@/components/common/VIcon'
import VMenu, { type VMenuActivatorProps } from '@/components/common/VMenu'
import useLocalization from '@/composables/localization/useLocalization'
import useBookingListItem from '@/composables/views/booking-list/useBookingListItem'
import { type Booking } from '@/types/Booking'
import { getStatusBgColor } from '@/utils/booking'

interface Props {
  item: Booking | DeepReadonly<Booking>
}

export default defineComponent<Props>((props) => {
  const { localeTexts } = useLocalization()
  const { cancelBookingModal, visitDate, bookingCancelModalParagraph, handleCancelBookingClick } = useBookingListItem(props)

  // #region Getters

  const statusClass = computed((): string => {
    return getStatusBgColor(props.item.booking_status)
  })

  const vip = computed((): string => {
    return props.item.is_patient_vip ? 'VIP' : ''
  })

  const isCancelBtnActive = computed((): boolean => {
    if (!props.item.booking_status) {
      return false
    }

    return props.item.booking_status === 'Pending' || props.item.booking_status === 'Approved'
  })

  // #endregion

  return () => (
    <RouterLink
      t-data='booking-list-table-row'
      class='table-row cursor-pointer border-b border-neutral-300 transition-colors hover:bg-neutral-100'
      to={`booking-details/${props.item.id}`}
    >
      <VTableBodyCell t-data='booking-list-table-row-id'>
        {props.item.booking_no}
      </VTableBodyCell>
      <VTableBodyCell t-data='booking-list-table-row-name'>
        {props.item.patient_first_name} {props.item.patient_last_name}
      </VTableBodyCell>
      <VTableBodyCell t-data='booking-list-table-row-vip'>
        {vip.value}
      </VTableBodyCell>
      <VTableBodyCell t-data='booking-list-table-row-visit-date'>
        {visitDate.value}
      </VTableBodyCell>
      <VTableBodyCell t-data='booking-list-table-row-status'>
        <VChip class={statusClass.value}>
          {props.item.booking_status}
        </VChip>
      </VTableBodyCell>
      <VTableBodyCell>
        <VMenu
          tData='booking-list-menu'
          menuContentClass={clsx('w-60 origin-top-right py-2')}
        >
          {{
            activator: (slotProps: VMenuActivatorProps) => (
              <VButton
                t-data='booking-list-menu-button'
                mainRef={slotProps.ref}
                class='btn-small'
                color={clsx('btn-white')}
                onClick={(e) => { e.preventDefault() }}
              >
                <VIcon
                  class='text-[24px]'
                  icon='material-symbols:more-vert'
                />
              </VButton>
            ),
            default: () => (
              <button
                t-data='cancel-booking-btn'
                class='menu-btn menu-btn-gray w-full'
                onClick={handleCancelBookingClick}
                disabled={!isCancelBtnActive.value}
              >
                <span>{localeTexts.value['agent-main-booking-list-menu-item-cancel-btn']}</span>
              </button>
            )
          }}
        </VMenu>
      </VTableBodyCell>
      {cancelBookingModal.isRevealed.value &&
        <PromptModal
          tData='cancel-booking-modal'
          modalkey={cancelBookingModal.key}
          title={localeTexts.value['booking-cancel-title']}
          content={bookingCancelModalParagraph.value}
          placeholder={localeTexts.value['booking-cancel-reason-placeholder']}
          cancelText={localeTexts.value['booking-cancel-cancel-btn']}
          confirmText={localeTexts.value['booking-cancel-confirm-btn']}
          confirmColor={clsx('btn-danger')}
        />}
    </RouterLink>
  )
}, {
  name: 'BookingListTableRow',
  props: ['item']
})
